import React from "react";
import { Loader2, X } from "lucide-react";
import { formatCoverage, formatSampleValue, formatTimestamp, useTraitDetail } from "../api";

const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wide mb-3">{title}</p>
    <div className="grid grid-cols-2 gap-x-4 gap-y-4">{children}</div>
  </div>
);

const Item: React.FC<{ label: string; children: React.ReactNode; span?: boolean }> = ({ label, children, span }) => (
  <div className={span ? "col-span-2" : ""}>
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className="text-sm text-gray-900 dark:text-gray-100 mt-1 break-words">{children}</p>
  </div>
);

// Read-only trait detail — GET /traits/{key}. This is a real drawer over the
// mock API, separate from the trait builder's local-only "Save and Use" step.
const TraitDetailDrawer: React.FC<{ traitKey: string | null; onClose: () => void }> = ({ traitKey, onClose }) => {
  const { data, loading, notFound, error } = useTraitDetail(traitKey);
  if (!traitKey) return null;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg h-full bg-white dark:bg-gray-900 shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-6 py-4 flex items-center justify-between z-10">
          <p className="text-lg text-gray-900 dark:text-gray-100">Trait Details</p>
          <button type="button" onClick={onClose} title="Close" className="text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">
            <X size={22} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {loading && (
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading trait…
            </p>
          )}
          {notFound && <p className="text-sm text-gray-500 dark:text-gray-400">This trait couldn't be found.</p>}
          {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}

          {data && (
            <>
              <div>
                <h2 className="text-2xl text-gray-900 dark:text-gray-100">{data.name}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{data.key}</p>
              </div>

              <Group title="Definition">
                <Item label="Category">{data.category_label}</Item>
                <Item label="Data Type">{data.data_type}</Item>
                <Item label="Description" span>{data.description || "—"}</Item>
              </Group>

              <Group title="Source">
                <Item label="Source Tables" span>{data.source_tables.length ? data.source_tables.join(", ") : "—"}</Item>
                <Item label="Source Fields" span>{data.source_fields.length ? data.source_fields.join(", ") : "—"}</Item>
              </Group>

              <Group title="Computation">
                <Item label="Logic" span>{data.logic || "—"}</Item>
                <Item label="Refresh">{data.refresh || "—"}</Item>
              </Group>

              <Group title="Quality">
                <Item label="Sample Value">{formatSampleValue(data.sample_value)}</Item>
                <Item label="Coverage">{formatCoverage(data.coverage)}</Item>
                <Item label="Status">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded ${data.status === "active" ? "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300" : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${data.status === "active" ? "bg-green-500" : "bg-gray-400"}`} />
                    {data.status === "active" ? "Active" : "Draft"}
                  </span>
                </Item>
                <Item label="Last Updated">{formatTimestamp(data.last_updated)}</Item>
              </Group>

              <p className="text-xs text-gray-400 dark:text-gray-500 border-t border-gray-100 dark:border-gray-800 pt-4">
                Demo data — this trait's values come from the traits mock API, not a live warehouse.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TraitDetailDrawer;
